<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>Why We Choose Linux</h2>
        <p>Giving new life to older hardware while teaching essential STEM skills</p>
      </div>
    </section>

    <section class="content-section">
      <div class="container">
        <h2 style="text-align: center;">Linux Powers Our Mission</h2>
        <p style="text-align: center; font-size: 1.2rem; max-width: 900px; margin: 0 auto 3rem; color: var(--text-light);">
          At Space Coast STEM, we install Linux (specifically <a href="https://linuxmint.com/" target="_blank" rel="noopener noreferrer">Linux Mint</a> with the Xfce desktop) on all refurbished machines unless there are special circumstances or requests. Here's why this decision benefits students, schools, and the environment.
        </p>

        <div class="feature-grid">
          <div class="feature-card">
            <div class="feature-icon">⚡</div>
            <div>
              <h3>Superior Performance</h3>
              <p>Linux is engineered for efficiency. While Windows and macOS have become increasingly resource-heavy, Linux delivers fast, responsive performance on both new and older hardware. Students get a modern operating system that runs smoothly without the distraction of unwanted Ads and newsfeeds.</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon">♻️</div>
            <div>
              <h3>Sustainable Technology</h3>
              <p>Linux's efficient design means hardware stays capable and relevant longer. Machines that might be considered "too old" for the latest Windows or macOS continue to run modern Linux perfectly, reducing e-waste while giving students fully functional, contemporary computers.</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon">🆓</div>
            <div>
              <h3>Zero Licensing Costs</h3>
              <p>Linux is completely free and open source. We don't have to pay for operating system licenses when refurbishing laptops, which means more resources can go directly toward helping students rather than software licensing fees.</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon">🔧</div>
            <div>
              <h3>Professional-Grade Software Ecosystem</h3>
              <p>Linux provides access to thousands of powerful, professional applications—all free. From development tools to creative suites to productivity software, students get the same industry-standard tools used by developers, engineers, and professionals worldwide, without any licensing costs.</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon">💼</div>
            <div>
              <h3>Industry-Standard Technology</h3>
              <p>Linux powers the modern world. It runs the majority of web servers, all supercomputers, cloud infrastructure, and smartphones (Android). By using Linux, students gain hands-on experience with the same operating system that drives global technology, from NASA to Google to Amazon.</p>
            </div>
          </div>

          <div class="feature-card">
            <div class="feature-icon">🎓</div>
            <div>
              <h3>Real-World Technical Experience</h3>
              <p>Using Linux teaches students problem-solving, technical literacy, and how professional computing environments work. These are transferable skills that benefit any STEM career path, from software engineering to data science to cybersecurity.</p>
            </div>
          </div>
        </div>

        <div class="distro-section">
          <h2 style="text-align: center; margin-bottom: 2rem;">Why Linux Mint (Xfce) Specifically?</h2>

          <div style="max-width: 800px; margin: 0 auto;">
            <p style="margin-bottom: 1.5rem;">We install <strong>Linux Mint</strong> with the <strong>Xfce</strong> desktop on the laptops we refurbish, for several important reasons:</p>

            <ul class="benefits-list">
              <li>
                <strong>Familiar, Intuitive Interface:</strong> Linux Mint uses a traditional menu-and-taskbar layout that anyone coming from Windows can pick up immediately, whether they're switching over or are new to computers entirely.
              </li>
              <li>
                <strong>Lightweight and Fast:</strong> The Xfce desktop is deliberately efficient, using a fraction of the memory of a typical modern desktop. That matters enormously on the older laptops we refurbish &mdash; it's the difference between a machine that feels sluggish and one that feels quick.
              </li>
              <li>
                <strong>Rock-Solid and Well Supported:</strong> Linux Mint is built on Ubuntu's long-term support releases, so each version receives years of security updates. It's one of the most widely used desktop Linux distributions in the world.
              </li>
              <li>
                <strong>Simple, Safe Software Installs:</strong> The Software Manager brings repository and Flatpak applications together in one trusted place, and the Update Manager keeps the whole system current with a single click &mdash; no hunting for installers.
              </li>
              <li>
                <strong>Extensive Software Library:</strong> Access to thousands of free, professional applications through the Software Manager, covering everything from productivity to programming to creative work.
              </li>
              <li>
                <strong>Excellent Documentation and Community:</strong> Linux Mint has a clear, beginner-friendly official user guide and a large, welcoming community, so students and parents can find answers on their own.
              </li>
            </ul>
          </div>
        </div>

        <div class="distro-section">
          <h2 style="text-align: center; margin-bottom: 2rem;">What About Zorin OS?</h2>

          <div style="max-width: 800px; margin: 0 auto;">
            <p style="margin-bottom: 1.5rem;">
              We previously installed <a href="https://zorin.com/os/education/" target="_blank" rel="noopener noreferrer">Zorin OS</a>, and plenty of students are still using laptops that run it.
            </p>

            <ul class="benefits-list">
              <li>
                <strong>Zorin OS laptops are still fully supported.</strong> If your laptop came with Zorin OS, you don't need to change a thing. We continue to maintain Zorin OS guides on our <router-link to="/support?section=zorin">Support &amp; Help</router-link> page alongside the Linux Mint ones.
              </li>
              <li>
                <strong>Why we standardized on Linux Mint.</strong> Mint's Xfce desktop runs lighter on the older hardware we refurbish, and its larger body of community documentation gives students more to lean on when they want to solve something themselves.
              </li>
              <li>
                <strong>Everything you learn carries over.</strong> Both are Ubuntu-based Linux distributions, so the terminal, the software, and the skills are the same on either machine.
              </li>
            </ul>
          </div>
        </div>

        <div class="alternatives-section">
          <h2>What About Windows or Mac?</h2>
          <p style="font-size: 1.1rem; max-width: 800px; margin: 0 auto; opacity: 0.95;">
            Linux provides the best experience for most students, combining professional capabilities with excellent performance. However, if you have specific software requirements for a particular class or program that requires Windows or macOS, we're happy to discuss alternatives. Just <router-link to="/contact" style="color: white; text-decoration: underline;">contact us</router-link> with your specific needs.
          </p>
        </div>

        <div style="margin-top: 4rem; text-align: center;">
          <h2 style="margin-bottom: 2rem;">Learn More About Linux</h2>
          <p style="font-size: 1.1rem; color: var(--text-light); margin-bottom: 2rem; max-width: 700px; margin-left: auto; margin-right: auto;">
            Want to learn more about using your Linux laptop? Check out our support resources and guides.
          </p>
          <div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">
            <router-link to="/support" class="btn btn-primary">Support & Help</router-link>
            <a href="https://linuxmint.com/documentation.php" target="_blank" rel="noopener noreferrer" class="btn btn-secondary">Linux Mint Documentation</a>
            <a href="https://help.zorin.com" target="_blank" rel="noopener noreferrer" class="btn btn-secondary">Zorin OS Documentation</a>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
// Why Linux view - static content
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
}

/* Content Section */
.content-section {
  padding: 5rem 2rem;
}

.content-section h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section a {
  color: var(--secondary-color, #3ba3d1);
  text-decoration: none;
}

.content-section a:hover {
  text-decoration: underline;
}

/* Feature Grid */
.feature-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  max-width: 1000px;
  margin: 0 auto 4rem;
}

.feature-card {
  display: flex;
  gap: 1.5rem;
  padding: 2rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.feature-icon {
  font-size: 3rem;
  flex-shrink: 0;
}

.feature-card h3 {
  margin: 0 0 0.75rem 0;
  color: var(--primary-color, #2c4a6b);
  font-size: 1.4rem;
  font-family: 'Poppins', sans-serif;
}

.feature-card p {
  margin: 0;
  color: var(--text-light, #666666);
  line-height: 1.7;
}

/* Distro Section */
.distro-section {
  background: var(--light-bg, #f5f7fa);
  padding: 3rem;
  border-radius: 10px;
  margin-top: 3rem;
}

.benefits-list {
  list-style: none;
  padding: 0;
}

.benefits-list li {
  padding: 1rem;
  background: white;
  margin-bottom: 1rem;
  border-radius: 8px;
  border-left: 4px solid var(--secondary-color, #3ba3d1);
  line-height: 1.7;
}

.benefits-list strong {
  color: var(--primary-color, #2c4a6b);
}

/* Alternatives Section */
.alternatives-section {
  margin-top: 4rem;
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  border-radius: 10px;
  color: white;
  text-align: center;
}

.alternatives-section h2 {
  color: white;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.btn-primary {
  background: var(--primary-color, #2c4a6b);
  color: var(--white, #ffffff);
}

.btn-primary:hover {
  background: var(--secondary-color, #3ba3d1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 74, 107, 0.3);
}

.btn-secondary {
  background: transparent;
  color: var(--primary-color, #2c4a6b);
  border: 2px solid var(--primary-color, #2c4a6b);
}

.btn-secondary:hover {
  background: var(--primary-color, #2c4a6b);
  color: var(--white, #ffffff);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero-content p {
    font-size: 1.1rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .content-section h2 {
    font-size: 2rem;
  }

  .feature-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}
</style>
